/**
 * COMPLAINT DASHBOARD — management view for Super Admin, Admin and Sales Head.
 *
 * Rebuilt on the single scoped `GET /dashboard/complaint` endpoint. It previously read a stub that
 * returned a raw `GROUP BY status`, and pulled its category / product / SLA series from three
 * `/ai/complaints/trends/*` endpoints. Those had two problems worth naming:
 *
 *  1. THEY WERE NOT ROW-SCOPED. A Sales Head opening this page saw national numbers in the trend
 *     charts and their own territories in the KPI band — two different populations on one screen.
 *  2. They could not honour a filter, so the date range, territory and product pickers this page
 *     needs would have moved the tiles and left the charts unchanged.
 *
 * Everything now comes from one query with one filter set and one scope, so every number on the page
 * counts the same complaints.
 */
import { useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import {
  AlertTriangle, CheckCircle2, Clock, Wrench, PauseCircle, ArrowUpCircle, Timer, ShieldAlert,
} from 'lucide-react';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { themedCatAxis, themedValAxis, themedLegendText, themedLabel } from '../../lib/chartTheme';
import {
  PageHeader, StatCard, Card, Alert, SkeletonStats, Skeleton, theme, Select, Table, Tag, Space,
  EmptyState, Segmented, DateRangePicker,
} from '../../ui';
import { formatIstDate } from '../../lib/time';
import { CountUp } from '../../ui/motion';
import { PALETTE } from '../../lib/palette';
// The State → District → Beat cascade the app already uses for every territory FILTER (the
// miller-column TerritoryPicker is for ASSIGNING territories, a different job). Reused rather than
// re-implemented, so "All districts" behaves identically on every screen that offers it.
import { TerritoryCascade, territoryIdOf, NO_TERRITORY, type TerritoryPick } from './SalesPerformancePage';
import { STATUS_LABEL } from '../complaints/complaintWorkflow';

const STATUS_COLOR: Record<string, string> = {
  OPEN: PALETTE.sky[500], IN_PROGRESS: PALETTE.harvest[400], AWAITING_INFO: PALETTE.plum[500],
  ESCALATED: PALETTE.danger[500], RESOLVED: PALETTE.brand[600], CLOSED: '#8c8c8c',
  REOPENED: PALETTE.plum[500], REJECTED: '#8c8c8c', CANCELLED: '#8c8c8c',
};

/** Preset windows. A complaint register is read in months, not days — 90 is the sensible default. */
const RANGES = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '6 months' },
  { value: '365', label: '1 year' },
  // Any window the presets do not cover — a quarter, last season — picked as From / To (no future dates).
  { value: 'custom', label: 'Custom' },
];

/** Local calendar day, not UTC: before 05:30 IST `toISOString()` still says yesterday. */
const iso = (d: Date | Dayjs) => dayjs(d).format('YYYY-MM-DD');
/** Hours → a human duration. "31.4" is a number; "1d 7h" is an answer. */
const dur = (h: number | null | undefined) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${Math.round(h * 10) / 10}h`;
  return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
};

export function ComplaintDashboard() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [days, setDays] = useState('90');
  const [custom, setCustom] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(89, 'day'), dayjs()]);
  const [territory, setTerritory] = useState<TerritoryPick>(NO_TERRITORY);
  const [productId, setProductId] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const territoryId = territoryIdOf(territory);
  const params = useMemo(() => ({
    from: days === 'custom' ? iso(custom[0]) : iso(new Date(Date.now() - Number(days) * 86400000)),
    to: days === 'custom' ? iso(custom[1]) : iso(new Date()),
    ...(territoryId ? { territoryId } : {}),
    ...(productId ? { productId } : {}),
    ...(categoryId ? { categoryId } : {}),
  }), [days, custom, territoryId, productId, categoryId]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-complaint', params],
    queryFn: () => request<any>({ url: '/dashboard/complaint', params }),
    placeholderData: (prev) => prev,
  });
  const { data: categories } = useQuery({
    queryKey: ['complaint-categories'],
    queryFn: () => request<any[]>({ url: '/complaint-categories' }),
  });
  const { data: products } = useQuery({
    queryKey: ['complaint-products', 'active'],
    queryFn: () => requestAllPages('/products', { active: 'true' }),
  });

  const header = (
    <PageHeader
      title="Complaint dashboard"
      subtitle="Service load, SLA performance and the product / batch hot spots behind them."
      icon={<Wrench />}
    />
  );

  if (isError) {
    return <div>{header}<Alert type="error" title="Couldn't load the complaint dashboard" description="Please retry in a moment." /></div>;
  }
  if (isLoading && !data) {
    return (
      <div>
        {header}
        <SkeletonStats count={4} className="mb-4" />
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-card" />)}
        </div>
      </div>
    );
  }

  const k = data?.kpi ?? {};
  const byStatus = data?.byStatus ?? [];
  const trend = data?.trend ?? [];

  const statusOption = {
    tooltip: { trigger: 'item' },
    legend: { orient: 'vertical', right: 0, top: 'middle', textStyle: themedLegendText(token) },
    series: [{
      type: 'pie', radius: ['42%', '70%'],
      itemStyle: { borderColor: token.colorBgContainer, borderWidth: 2 },
      data: byStatus.map((s: any) => ({
        name: STATUS_LABEL[s.status] ?? s.status,
        value: Number(s.count),
        itemStyle: { color: STATUS_COLOR[s.status] ?? '#8c8c8c' },
      })),
      label: { formatter: '{b}\n{c}', ...themedLabel(token) },
    }],
  };

  const trendOption = {
    grid: { left: 44, right: 16, top: 30, bottom: 32 },
    legend: { top: 0, textStyle: themedLegendText(token) },
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: trend.map((t: any) => String(t.day).slice(5, 10)), ...themedCatAxis(token) },
    yAxis: { type: 'value', ...themedValAxis(token) },
    series: [
      { name: 'Raised', type: 'line', smooth: true, showSymbol: false, data: trend.map((t: any) => Number(t.raised)), itemStyle: { color: PALETTE.danger[500] }, areaStyle: { opacity: 0.08 } },
      { name: 'Resolved', type: 'line', smooth: true, showSymbol: false, data: trend.map((t: any) => Number(t.resolved)), itemStyle: { color: PALETTE.brand[600] }, areaStyle: { opacity: 0.08 } },
    ],
  };

  const barOption = (rows: any[], key: string, colour: string) => ({
    grid: { left: 130, right: 24, top: 14, bottom: 28 },
    xAxis: { type: 'value', ...themedValAxis(token) },
    yAxis: { type: 'category', data: rows.slice(0, 8).map((r) => r[key]).reverse(), ...themedCatAxis(token) },
    tooltip: { trigger: 'axis' },
    series: [{
      type: 'bar', barMaxWidth: 16,
      itemStyle: { color: colour, borderRadius: [0, 3, 3, 0] },
      data: rows.slice(0, 8).map((r) => Number(r.count)).reverse(),
    }],
  });

  const goList = (extra: Record<string, string> = {}) => {
    const q = new URLSearchParams({ view: 'list', ...extra });
    navigate(`/complaints?${q.toString()}`);
  };

  return (
    <div>
      {header}

      {/* Filters, in the order the question is usually asked: when · where · what. */}
      <Card className="mb-4" density="compact">
        <Space wrap size={8}>
          <Segmented value={days} onChange={(v) => setDays(String(v))} options={RANGES} />
          {days === 'custom' && <DateRangePicker value={custom} onChange={setCustom} />}
          <TerritoryCascade facets={data?.facets?.territories ?? []} pick={territory} set={setTerritory} />
          <Select
            showSearch optionFilterProp="label" style={{ width: 200 }}
            value={productId} onChange={(v) => setProductId(String(v))}
            options={[{ value: '', label: 'All products' },
              ...(products?.data ?? []).map((p: any) => ({ value: String(p.id), label: `${p.name} · ${p.sku}` }))]}
          />
          <Select
            showSearch optionFilterProp="label" style={{ width: 190 }}
            value={categoryId} onChange={(v) => setCategoryId(String(v))}
            options={[{ value: '', label: 'All categories' },
              ...(categories ?? []).map((c: any) => ({ value: String(c.id), label: c.name }))]}
          />
          {/* The dates the figures below were counted over, in words — the server's echo, so the
              caption cannot drift from what was actually queried. */}
          <span className="type-caption text-muted">
            {formatIstDate(data?.from ?? params.from)} → {formatIstDate(data?.to ?? params.to)}
          </span>
        </Space>
      </Card>

      {/* Load. Every tile is a link into the register, filtered the same way. */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Raised" tone="neutral" icon={<Wrench />} value={<CountUp value={k.total ?? 0} />} hint="In this period" />
        <StatCard label="Open" tone="sky" icon={<AlertTriangle />} value={<CountUp value={k.open ?? 0} />} hint="Not yet finished" onClick={() => goList()} />
        <StatCard label="Awaiting info" tone="neutral" icon={<PauseCircle />} value={<CountUp value={k.awaitingInfo ?? 0} />} hint="SLA paused" onClick={() => goList({ status: 'AWAITING_INFO' })} />
        <StatCard label="Escalated" tone="danger" icon={<ArrowUpCircle />} value={<CountUp value={k.escalated ?? 0} />} hint="With someone senior" onClick={() => goList({ status: 'ESCALATED' })} />
        <StatCard label="SLA at risk" tone="warning" icon={<Timer />} value={<CountUp value={k.slaRisk ?? 0} />} hint="Due within 24h" onClick={() => goList()} />
        <StatCard label="SLA breached" tone="danger" icon={<AlertTriangle />} value={<CountUp value={k.slaBreached ?? 0} />} hint={k.slaBreached ? 'Needs attention' : 'On track'} onClick={() => goList({ breached: 'true' })} />
      </div>

      {/* Speed. Two resolution clocks on purpose — see the endpoint's own note. */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Avg first response" tone="brand" icon={<Clock />} value={dur(k.avgResponseHours)} hint="Raised → somebody acted" />
        <StatCard label="Avg resolution" tone="brand" icon={<CheckCircle2 />} value={dur(k.avgResolutionHours)} hint="Raised → resolved, wall clock" />
        <StatCard
          label="Avg working time" tone="success" icon={<Timer />} value={dur(k.avgWorkingResolutionHours)}
          hint="Excludes time waiting on the customer"
        />
        <StatCard label="To close" tone="success" icon={<CheckCircle2 />} value={<CountUp value={k.awaitingClosure ?? 0} />} hint="Resolved, awaiting sign-off" onClick={() => goList({ status: 'RESOLVED' })} />
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_1.5fr]">
        <Card title="By status">
          <ReactECharts option={statusOption} style={{ height: 200 }} />
        </Card>
        <Card title="Raised vs resolved" extra={<span className="type-caption text-muted">Daily</span>}>
          <ReactECharts option={trendOption} style={{ height: 200 }} />
        </Card>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card title="By category">
          {(data?.byCategory ?? []).length
            ? <ReactECharts option={barOption(data.byCategory, 'category', PALETTE.harvest[400])} style={{ height: 200 }} />
            : <EmptyState title="No complaints in this period" />}
        </Card>
        <Card title="Where they come from" extra={<span className="type-caption text-muted">Source channel</span>}>
          {(data?.bySource ?? []).length
            ? <ReactECharts option={barOption(data.bySource, 'source', PALETTE.sky[500])} style={{ height: 200 }} />
            : <EmptyState title="No complaints in this period" />}
        </Card>
      </div>

      {/*
        * PRODUCT AND BATCH INTELLIGENCE — the reason a manufacturer reads this page at all. Fifteen
        * complaints spread across a product's history is noise; fifteen against ONE batch is a lot to
        * pull, which is why the batch table carries the districts it was complained about in.
        */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card title="Products complained about" extra={<span className="type-caption text-muted">Top 10</span>}>
          <Table
            rowKey="productId" size="small" dataSource={data?.byProduct ?? []} pagination={false}
            locale={{ emptyText: <EmptyState title="Nothing in this period" /> }}
            onRow={(r: any) => ({ onClick: () => goList({ productId: String(r.productId) }), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Product', dataIndex: 'product', ellipsis: true,
                render: (v: string, r: any) => <div><div className="type-body-medium">{v}</div><div className="type-caption text-muted">{r.sku}</div></div> },
              { title: 'Batches', dataIndex: 'batches', width: 82, align: 'right' as const },
              { title: 'Open', dataIndex: 'open', width: 70, align: 'right' as const },
              { title: 'Total', dataIndex: 'count', width: 70, align: 'right' as const,
                render: (v: any) => <b>{v}</b> },
            ]}
          />
        </Card>

        <Card title="Batches complained about" extra={<span className="type-caption text-muted">Top 10</span>}>
          <Table
            rowKey="batchNo" size="small" dataSource={data?.byBatch ?? []} pagination={false}
            locale={{ emptyText: <EmptyState title="No batch was named" /> }}
            onRow={(r: any) => ({ onClick: () => goList({ batchNo: r.batchNo }), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Batch', dataIndex: 'batchNo', width: 150,
                render: (v: string, r: any) => <div><div className="type-body-medium">{v}</div><div className="type-caption text-muted">{r.product}</div></div> },
              { title: 'Districts', dataIndex: 'districts', ellipsis: true,
                render: (v: string, r: any) => (v
                  // A batch complained about in several districts is a manufacturing problem; one
                  // district is more likely a storage or transport problem. Worth showing.
                  ? <span>{v}{Number(r.territories) > 1 ? <Tag tone="red" className="ml-1">{r.territories} districts</Tag> : null}</span>
                  : <span className="text-subtle">—</span>) },
              { title: 'Total', dataIndex: 'count', width: 70, align: 'right' as const, render: (v: any) => <b>{v}</b> },
            ]}
          />
        </Card>
      </div>

      {/*
        * Batch codes read off a pack that exist nowhere in stock. Given its own card, in danger tone,
        * because the response is completely different from every other number on this page: it is a
        * possible counterfeit or mislabelled lot, not a service backlog.
        */}
      {(data?.unknownBatches ?? []).length > 0 && (
        <Card
          className="mt-4"
          title={<Space size={6}><ShieldAlert size={16} /><span>Batch codes we have no record of</span></Space>}
          extra={<span className="type-caption text-danger-600">Possible counterfeit or mislabelled stock</span>}
        >
          <p className="type-caption mb-2 text-muted">
            Customers reported these batch codes on packs they received, and none of them exists in stock.
            Verify the physical stock before settling any of these claims.
          </p>
          <Table
            rowKey="batchNo" size="small" dataSource={data.unknownBatches} pagination={false}
            columns={[
              { title: 'Batch on the pack', dataIndex: 'batchNo', width: 200, render: (v: string) => <Tag tone="red">{v}</Tag> },
              { title: 'Reported against', dataIndex: 'products', ellipsis: true },
              { title: 'Reports', dataIndex: 'count', width: 90, align: 'right' as const, render: (v: any) => <b>{v}</b> },
            ]}
          />
        </Card>
      )}

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card title="By territory" extra={<span className="type-caption text-muted">Top 15</span>}>
          <Table
            rowKey="territoryId" size="small" dataSource={data?.byTerritory ?? []} pagination={false}
            locale={{ emptyText: <EmptyState title="No territory data" /> }}
            columns={[
              { title: 'Territory', dataIndex: 'territory', ellipsis: true, render: (v: string) => v ?? '—' },
              { title: 'Open', dataIndex: 'open', width: 80, align: 'right' as const },
              { title: 'Total', dataIndex: 'count', width: 80, align: 'right' as const, render: (v: any) => <b>{v}</b> },
            ]}
          />
        </Card>
        <Card title="Quality of outcome">
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Reopened" tone="warning" value={<CountUp value={k.reopened ?? 0} />} hint="Came back after an answer" />
            <StatCard label="Rejected" tone="neutral" value={<CountUp value={k.rejected ?? 0} />} hint="Invalid or duplicate" />
            <StatCard label="Withdrawn" tone="neutral" value={<CountUp value={k.cancelled ?? 0} />} hint="Or never substantiated" />
            <StatCard label="Avg rating" tone="brand" value={k.avgRating != null ? `${k.avgRating} / 5` : '—'} hint="Where customers rated" />
          </div>
        </Card>
      </div>
    </div>
  );
}
